import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, X, Info } from 'lucide-react';

const QuoteModal = ({ onClose, onSubmitQuote, user, fullUserData, quoteToEdit }) => {
    const [items, setItems] = useState([{ description: '', price: '' }]);

    const isEditing = !!quoteToEdit;

    useEffect(() => {
        if (isEditing && quoteToEdit.quote.items) {
            setItems(quoteToEdit.quote.items);
        }
    }, [isEditing, quoteToEdit]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const handleAddItem = () => {
        setItems([...items, { description: '', price: '' }]);
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const total = useMemo(() => {
        return items.reduce((acc, item) => acc + (parseFloat(item.price) || 0), 0);
    }, [items]);

    const isLowValue = total > 0 && total < 10;

    const handleSubmit = () => {
        const validItems = items.filter(item => item.description && item.price > 0);
        if (validItems.length === 0) {
            alert('Por favor, adicione pelo menos um item válido ao orçamento.');
            return;
        }

        onSubmitQuote({
            items: validItems,
            total: total,
            serviDetails: quoteToEdit?.quote.serviDetails || {
                name: fullUserData?.profile_servi?.companyName || fullUserData?.profile_servi?.name,
                uid: user.uid
            }
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg relative animate-slide-up flex flex-col shadow-2xl" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
                <header className="p-5 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
                    <div>
                        <h2 className="text-xl font-black text-gray-800 uppercase tracking-tighter">{isEditing ? 'Editar Orçamento' : 'Novo Orçamento'}</h2>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Preencha os detalhes do serviço</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </header>

                <main className="p-5 flex-grow overflow-y-auto space-y-4">
                    {/* AVISO DE VALOR MÍNIMO PARA O SERVI */}
                    {isLowValue && (
                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex items-start animate-fade-in mb-2">
                            <Info size={18} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <p className="text-[11px] text-blue-800 leading-relaxed font-medium">
                                <strong className="block mb-0.5 uppercase tracking-widest">Atenção ao Valor Mínimo</strong>
                                O valor mínimo para a cobrança final/pagamento na T-SERVI é de <b>R$ 10,00</b>. Considere isso ao definir o preço da sua proposta.
                            </p>
                        </div>
                    )}

                    <div className="space-y-3">
                        {items.map((item, index) => (
                            <div key={index} className="flex items-center space-x-2 animate-slide-right">
                                <input
                                    type="text"
                                    value={item.description}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    placeholder="Descrição do serviço..."
                                    className="flex-grow p-3 text-sm bg-gray-50 border-2 border-transparent rounded-xl focus:border-blue-500 focus:bg-white outline-none transition-all font-semibold"
                                />
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-black text-gray-400">R$</span>
                                    <input
                                        type="number"
                                        value={item.price}
                                        onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                        placeholder="0,00"
                                        className="w-24 pl-7 p-3 text-sm bg-gray-50 border-2 border-transparent rounded-xl focus:border-blue-500 focus:bg-white font-black outline-none transition-all"
                                    />
                                </div>
                                <button onClick={() => handleRemoveItem(index)} className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-all">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <button onClick={handleAddItem} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center text-blue-600 font-bold text-xs hover:bg-blue-50 transition-all active:scale-[0.98]">
                        <Plus size={16} className="mr-2" /> ADICIONAR ITEM
                    </button>
                </main>

                <footer className="p-6 bg-gray-50 border-t rounded-b-2xl">
                    <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center mb-4">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Estimado</span>
                        <span className="text-2xl font-black text-gray-900 tracking-tighter">R$ {total.toFixed(2).replace('.', ',')}</span>
                    </div>
                    <button
                        onClick={handleSubmit}
                        className="w-full py-4 bg-blue-600 text-white font-black text-sm rounded-2xl shadow-xl shadow-blue-100 hover:bg-blue-700 active:scale-[0.98] transition-all uppercase tracking-widest"
                    >
                        {isEditing ? 'Atualizar Proposta' : 'Enviar Proposta'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default QuoteModal;
